import { gql, useLazyQuery } from "@apollo/client";
import { useCallback } from "react";
import useCurrentLocation, { Location } from "./useCurrentLocation";
import { getDistanceFromLatLonInKm } from "../utils/utils";

type VehicleRentalStationItem = {
    stationId: string;
    lat: number | null;
    lon: number | null;
    operative: boolean | null;
};

type VehicleRentalStationsQuery = {
    vehicleRentalStations: Array<VehicleRentalStationItem | null>;
};

export const ALL_STATIONS = gql`
    query AllStations {
        vehicleRentalStations {
            stationId
            lat
            lon
            operative
        }
    }
`;

// Ids of the operative stations closest to location, nearest first.
export const findClosestStationIds = (
    stations: Array<VehicleRentalStationItem | null>,
    location: Location,
    count: number
) =>
    stations
        .filter(
            (station): station is VehicleRentalStationItem & {
                lat: number;
                lon: number;
            } =>
                !!station &&
                station.operative === true &&
                station.lat != null &&
                station.lon != null
        )
        .map((station) => ({
            stationId: station.stationId,
            distance: getDistanceFromLatLonInKm(
                location.latitude,
                location.longitude,
                station.lat,
                station.lon
            ),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, count)
        .map((station) => station.stationId);

const useNearbyStations = () => {
    const { location, error: locationError, locating, locate } =
        useCurrentLocation();
    const [loadStations, { loading: loadingStations, error: stationsError }] =
        useLazyQuery<VehicleRentalStationsQuery>(ALL_STATIONS, {
            fetchPolicy: "network-only",
        });

    // Resolves with the ids of the count closest stations, or undefined when
    // the location or the stations couldn't be loaded.
    const findNearby = useCallback(
        async (count: number) => {
            const [currentLocation, result] = await Promise.all([
                locate(),
                loadStations(),
            ]);
            const stations = result.data?.vehicleRentalStations;
            if (!currentLocation || !stations) return undefined;
            return findClosestStationIds(stations, currentLocation, count);
        },
        [locate, loadStations]
    );

    return {
        findNearby,
        location,
        locationError,
        stationsError,
        searching: locating || loadingStations,
    };
};

export default useNearbyStations;
